import React, { useState, useMemo } from 'react';
import { VehicleBooking } from '../../types/vehicle';
import { Timestamp } from 'firebase/firestore';
import { 
  ChevronLeftIcon, 
  ChevronRightIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  format, 
  isSameDay,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  isSameMonth,
  isWithinInterval
} from 'date-fns';
import { hu } from 'date-fns/locale';

interface BookingsCalendarProps {
  bookings: VehicleBooking[];
  onBookingClick?: (booking: VehicleBooking) => void;
  onBookingDelete?: (bookingId: string, bookingInfo: string) => void;
}

const BookingsCalendar: React.FC<BookingsCalendarProps> = ({ bookings, onBookingClick, onBookingDelete }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const toDate = (date: Date | Timestamp): Date => {
    return date instanceof Timestamp ? date.toDate() : date;
  };

  // Hónap napjai
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  // Foglalások napokhoz rendelése
  const bookingsByDay = useMemo(() => {
    const map = new Map<string, VehicleBooking[]>();
    
    bookings.forEach(booking => {
      const start = toDate(booking.startDate);
      const end = toDate(booking.endDate);
      
      calendarDays.forEach(day => {
        if (isWithinInterval(day, { start, end })) {
          const key = format(day, 'yyyy-MM-dd');
          const existing = map.get(key) || [];
          map.set(key, [...existing, booking]);
        }
      });
    });
    
    return map;
  }, [bookings, calendarDays]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500';
      case 'confirmed':
        return 'bg-green-500';
      case 'in_progress':
        return 'bg-blue-500';
      case 'completed':
        return 'bg-gray-400';
      case 'cancelled':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const handlePrevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const handleToday = () => {
    setCurrentMonth(new Date());
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CalendarIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {format(currentMonth, 'yyyy. MMMM', { locale: hu })}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleToday}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              Ma
            </button>
            <button
              onClick={handlePrevMonth}
              className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <button
              onClick={handleNextMonth}
              className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="p-4">
        {/* Weekday Headers */}
        <div className="grid grid-cols-7 gap-2 mb-2">
          {['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'].map((day, index) => (
            <div
              key={index}
              className="text-center text-sm font-semibold text-gray-600 dark:text-gray-400 py-2"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-2">
          {calendarDays.map((day, index) => {
            const dayKey = format(day, 'yyyy-MM-dd');
            const dayBookings = bookingsByDay.get(dayKey) || [];
            const isToday = isSameDay(day, new Date());
            const isCurrentMonth = isSameMonth(day, currentMonth);

            return (
              <div
                key={index}
                className={`min-h-[100px] p-2 border rounded-lg ${
                  isCurrentMonth
                    ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                    : 'bg-gray-50 dark:bg-gray-900/50 border-gray-100 dark:border-gray-800'
                } ${isToday ? 'ring-2 ring-blue-500' : ''}`}
              >
                <div className={`text-sm font-medium mb-1 ${
                  isToday 
                    ? 'text-blue-600 dark:text-blue-400 font-bold' 
                    : isCurrentMonth
                    ? 'text-gray-900 dark:text-white'
                    : 'text-gray-400 dark:text-gray-600'
                }`}>
                  {format(day, 'd')}
                </div>

                {/* Bookings for this day */}
                <div className="space-y-1">
                  {dayBookings.slice(0, 3).map((booking, idx) => {
                    const start = toDate(booking.startDate);
                    const end = toDate(booking.endDate);
                    const isStartDay = isSameDay(day, start);
                    
                    return (
                      <div
                        key={`${booking.id}-${idx}`}
                        className="group relative"
                      >
                        <div
                          onClick={() => onBookingClick?.(booking)}
                          className={`text-xs px-1.5 py-0.5 rounded cursor-pointer hover:opacity-80 transition-opacity truncate ${getStatusColor(booking.status)} text-white`}
                          title={`🚗 ${booking.vehicleLicensePlate || `${booking.vehicleMake} ${booking.vehicleModel}`}\n👤 ${booking.userName}\n📞 ${booking.userPhone}\n📧 ${booking.userEmail}\n📅 ${format(start, 'yyyy. MMM dd.', { locale: hu })} - ${format(end, 'yyyy. MMM dd.', { locale: hu })}\n💰 ${booking.totalPrice?.toLocaleString()} ${booking.currency || 'HUF'}\n\n💡 Bal klikk = szerkesztés\n🗑️ Jobb klikk = törlés`}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            if (onBookingDelete) {
                              onBookingDelete(
                                booking.id,
                                `${booking.vehicleLicensePlate || `${booking.vehicleMake} ${booking.vehicleModel}`} - ${booking.userName} (${format(start, 'MMM dd', { locale: hu })} - ${format(end, 'MMM dd', { locale: hu })})`
                              );
                            }
                          }}
                        >
                          <span className={isStartDay ? 'font-semibold' : ''}>
                            {booking.vehicleLicensePlate || `${booking.vehicleMake} ${booking.vehicleModel}`}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  {dayBookings.length > 3 && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 px-1.5">
                      +{dayBookings.length - 3} több
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-yellow-500"></div>
            <span className="text-gray-700 dark:text-gray-300">Függőben</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-green-500"></div>
            <span className="text-gray-700 dark:text-gray-300">Jóváhagyva</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-blue-500"></div>
            <span className="text-gray-700 dark:text-gray-300">Folyamatban</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-gray-400"></div>
            <span className="text-gray-700 dark:text-gray-300">Befejezett</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-red-500"></div>
            <span className="text-gray-700 dark:text-gray-300">Törölve</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingsCalendar;
